import { useEffect, useId, useRef, useState } from 'react'

import { cn } from '../../lib/cn'
import s from './Wheel.module.css'

/**
 * Roue de sélection — report de `.wheel` du prototype validé, la commande de la boîte de
 * rendez-vous : une colonne qui défile, l'item retenu centré sous une bande, les voisins qui
 * s'effacent en haut et en bas (`mocks/ghp-bo/app.css`, comportement dans `initRdvWheel`).
 *
 * ⚠️ **La géométrie est celle du prototype ; la sémantique ne l'est pas, et c'est délibéré.** Ses
 * items sont des `<div>` dans un conteneur qui défile : seule la souris les choisit, rien ne
 * s'annonce, et le clavier ne les atteint pas. Le back-office est une application de bureau où le
 * créneau est la seule donnée qu'on saisit dans cette boîte — une commande que le clavier ne
 * touche pas y est inutilisable. C'est donc une **liste de choix** réelle, avec ses flèches et son
 * état annoncé. Même arbitrage que `Checkbox`, qui habille une primitive sous la peau du prototype.
 *
 * **Contrôlée seulement** (`value` + `onValueChange`), comme `Checkbox` et `Switch` : le créneau
 * appartient à la boîte qui l'enregistre. Un état interne le ferait bouger à l'œil avant que le
 * serveur ait accepté, et rester bougé s'il refuse.
 *
 * **Les gestes à la souris** (demande du développeur, 23/09 : « ça saute trop ») : on **glisse**
 * et l'item qui s'arrête sous la bande est retenu, comme au prototype ; un cran de **molette**
 * avance d'**un** item ; un **clic** avance d'**un cran** vers l'item visé, au-dessus ou en
 * dessous ; un clic sur l'item retenu ne fait rien. Le prototype, lui, sautait jusqu'à l'item
 * cliqué, et laissait la molette native défiler d'une centaine de pixels par cran — deux items
 * et demi.
 */

/** La hauteur d'un item — `ITEM = 44` de `initRdvWheel`, et `--wheel-item` de la feuille. */
const ITEM_PX = 44

/** Le silence qui clôt un défilement — les 110 ms de `initRdvWheel`. */
const SETTLE_MS = 110

/** En deçà, une pression est un clic qui tremble, pas un glisser. */
const DRAG_THRESHOLD_PX = 4

/**
 * Le trajet de molette qui vaut un item. Un cran de souris le franchit d'un coup (Chrome en
 * compte cent pixels) ; un trackpad, qui émet par petits pas, avance au rythme du doigt.
 */
const WHEEL_STEP_PX = 40

function reducedMotion(): boolean {
  return (
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  )
}

/**
 * ⚠️ `typeof … === 'function'` : jsdom n'implémente pas `Element.scrollTo`, et la boîte de
 * rendez-vous se teste en changeant la valeur — l'effet de placement tournerait sur un appel absent.
 */
function scrollToTop(node: HTMLElement, top: number, animate: boolean): void {
  if (typeof node.scrollTo === 'function') {
    node.scrollTo({ top, behavior: animate && !reducedMotion() ? 'smooth' : 'instant' })
  } else {
    node.scrollTop = top
  }
}

interface Drag {
  pointerY: number
  scrollTop: number
  moved: boolean
}

export interface WheelItem {
  value: string
  label: string
}

export interface WheelProps {
  /** Ce que la colonne choisit — « Jour », « Heure ». Nomme la liste pour la synthèse vocale. */
  label: string
  items: readonly WheelItem[]
  value: string
  /** Sans rappel, la roue se présente désarmée — « pas câblé » se voit, comme partout ailleurs. */
  onValueChange?: (value: string) => void
  className?: string
}

export function Wheel({ label, items, value, onValueChange, className }: WheelProps) {
  const labelId = useId()
  const optionId = useId()
  const scroller = useRef<HTMLDivElement>(null)
  const index = items.findIndex((item) => item.value === value)
  const disabled = onValueChange === undefined
  const placed = useRef(false)
  const settle = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const drag = useRef<Drag | null>(null)
  const swallowClick = useRef(false)
  const [dragging, setDragging] = useState(false)
  /*
   * Le rang vers lequel la roue se dirige, avancé avant le rendu : deux crans de molette plus
   * rapprochés qu'un rendu partiraient sinon du même rang, et n'en feraient qu'un.
   */
  const heading = useRef(index)
  /*
   * Le rang sous la bande quand **rien n'est retenu**, relevé au placement. ⚠️ Au repos, un
   * navigateur pose la roue à un pixel de son cran, et la recale au moindre rendu de la boîte —
   * en émettant un défilement que personne n'a fait. Le retenir posait un créneau jamais choisi :
   * « Gérer une demande » enregistrait un rendez-vous dès qu'on changeait l'équipe.
   */
  const resting = useRef<number | null>(null)

  heading.current = index

  const clampIndex = (at: number) => Math.max(0, Math.min(items.length - 1, at))

  /** Le rang sous la bande, lu dans la position — ce que le prototype appelle `idx`. */
  const centered = (node: HTMLElement) => clampIndex(Math.round(node.scrollTop / ITEM_PX))

  /*
   * Amener l'item retenu sous la bande. `index * 44` est le calcul du prototype : le rembourrage
   * de 53px fait le centrage, il n'entre pas dans la position. ⚠️ C'est exactement ce que la
   * version précédente comptait en trop (`offsetTop` inclut le rembourrage) : l'item retenu
   * s'arrêtait un cran plus bas que celui qu'on venait de choisir.
   *
   * À l'ouverture, sans animation : c'est l'état de la boîte, pas un mouvement demandé. Ensuite,
   * en glissant. Et rien quand l'item est déjà sous la bande — c'est le cas quand la valeur vient
   * du défilement lui-même, qu'un recentrage viendrait contrarier.
   *
   * ⚠️ Dépend de `index` et non de `value` : deux jours différents au même rang n'existent pas,
   * mais une liste rechargée, oui — et c'est le rang qui place.
   */
  useEffect(() => {
    const node = scroller.current

    if (node === null) return

    resting.current = index < 0 ? centered(node) : null

    if (index < 0) return

    if (Math.round(node.scrollTop / ITEM_PX) !== index) {
      scrollToTop(node, index * ITEM_PX, placed.current)
    }

    placed.current = true
  }, [index])

  useEffect(() => () => clearTimeout(settle.current), [])

  /*
   * ⚠️ Écouteur natif, pas `onWheel` : React pose les siens en `passive`, et un écouteur passif ne
   * peut pas empêcher la molette de faire défiler — la roue avancerait deux fois, d'un item et de
   * cent pixels.
   */
  const step = useRef<(direction: number) => void>(() => undefined)

  step.current = (direction) => {
    const node = scroller.current
    const from = heading.current < 0 && node !== null ? centered(node) : heading.current
    const to = clampIndex(from + direction)
    const next = items[to]

    if (next === undefined || to === heading.current) return

    heading.current = to
    onValueChange?.(next.value)
  }

  useEffect(() => {
    const node = scroller.current

    if (node === null || disabled) return

    let travelled = 0

    const onWheel = (event: WheelEvent) => {
      event.preventDefault()
      travelled +=
        event.deltaMode === WheelEvent.DOM_DELTA_PIXEL ? event.deltaY : event.deltaY * ITEM_PX

      if (Math.abs(travelled) < WHEEL_STEP_PX) return

      step.current(Math.sign(travelled))
      travelled = 0
    }

    node.addEventListener('wheel', onWheel, { passive: false })

    return () => {
      node.removeEventListener('wheel', onWheel)
    }
  }, [disabled])

  /**
   * Les quatre déplacements d'une liste de choix. ⚠️ **Elle ne boucle pas** : passer du premier
   * créneau au dernier d'une flèche ferait sauter du 3 novembre au 28 sans que rien ne le dise.
   */
  const move = (to: number) => {
    const clamped = clampIndex(to)
    const next = items[clamped]

    if (next !== undefined && clamped !== index) onValueChange?.(next.value)
  }

  const endDrag = (node: HTMLElement, released: boolean) => {
    const current = drag.current

    drag.current = null

    if (current === null || !current.moved) return

    setDragging(false)
    scrollToTop(node, centered(node) * ITEM_PX, true)

    /*
     * Le relâchement d'un glisser émet aussi un `click` — sur la liste, qui a capturé le pointeur.
     * Il avancerait d'un cran ce qu'on vient de poser. Le drapeau tombe au tour suivant quoi
     * qu'il arrive : un clic qui ne viendrait pas ne doit pas avaler le prochain.
     */
    if (released) {
      swallowClick.current = true
      setTimeout(() => {
        swallowClick.current = false
      }, 0)
    }
  }

  return (
    <div className={cn(s.root, className)}>
      <span className={s.label} id={labelId}>
        {label}
      </span>
      <div className={s.wheel}>
        <span className={s.band} aria-hidden />
        <span className={cn(s.fade, s.fadeTop)} aria-hidden />
        <span className={cn(s.fade, s.fadeBottom)} aria-hidden />
        <div
          ref={scroller}
          className={s.scroll}
          role="listbox"
          aria-labelledby={labelId}
          /*
           * ⚠️ **La liste est le seul arrêt de tabulation, et ses options n'en sont pas** : c'est
           * le motif que l'ARIA recommande pour une liste de choix, et c'est `aria-activedescendant`
           * qui dit laquelle est active. Donner un `tabIndex` à chaque option ferait vingt-et-un
           * arrêts de tabulation pour choisir une heure.
           */
          aria-activedescendant={index < 0 ? undefined : `${optionId}-${String(index)}`}
          aria-disabled={disabled || undefined}
          data-dragging={dragging || undefined}
          tabIndex={disabled ? -1 : 0}
          onScroll={(event) => {
            if (disabled) return

            const node = event.currentTarget

            clearTimeout(settle.current)
            settle.current = setTimeout(() => {
              const at = centered(node)

              if (at !== resting.current) move(at)
            }, SETTLE_MS)
          }}
          /*
           * Le glisser à la souris. Au doigt et au trackpad, le défilement natif le fait déjà ;
           * à la souris, il n'existe pas — c'est la molette ou rien.
           */
          onPointerDown={(event) => {
            if (disabled || event.pointerType !== 'mouse' || event.button !== 0) return

            drag.current = {
              pointerY: event.clientY,
              scrollTop: event.currentTarget.scrollTop,
              moved: false,
            }
          }}
          onPointerMove={(event) => {
            const current = drag.current

            if (current === null) return

            const dy = event.clientY - current.pointerY

            if (!current.moved) {
              if (Math.abs(dy) <= DRAG_THRESHOLD_PX) return

              current.moved = true
              event.currentTarget.setPointerCapture(event.pointerId)
              setDragging(true)
            }

            scrollToTop(event.currentTarget, current.scrollTop - dy, false)
          }}
          onPointerUp={(event) => {
            endDrag(event.currentTarget, true)
          }}
          onPointerCancel={(event) => {
            endDrag(event.currentTarget, false)
          }}
          onClickCapture={(event) => {
            if (!swallowClick.current) return

            swallowClick.current = false
            event.stopPropagation()
          }}
          onKeyDown={(event) => {
            if (disabled) return

            const step = { ArrowDown: 1, ArrowUp: -1 }[event.key]

            if (step !== undefined) {
              // Sans ça, la flèche fait aussi défiler la page derrière la boîte.
              event.preventDefault()
              move(index + step)
              return
            }

            if (event.key === 'Home') {
              event.preventDefault()
              move(0)
            }

            if (event.key === 'End') {
              event.preventDefault()
              move(items.length - 1)
            }
          }}
        >
          {items.map((item, at) => (
            // eslint-disable-next-line jsx-a11y/interactive-supports-focus -- cf. `aria-activedescendant`
            <div
              key={item.value}
              id={`${optionId}-${String(at)}`}
              className={s.item}
              role="option"
              aria-selected={at === index}
              onClick={() => {
                // Un cran vers l'item visé ; aucun sur l'item retenu. Sans valeur retenue, le
                // repère est ce qui se trouve sous la bande.
                const from =
                  index < 0 && scroller.current !== null ? centered(scroller.current) : index

                // Sans valeur retenue, l'item sous la bande se retient d'un clic : il est affiché
                // comme le choix, le cliquer ne peut pas ne rien faire.
                if (index < 0 && at === from) move(at)
                else if (at !== from) move(from + Math.sign(at - from))
              }}
            >
              {item.label}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
